<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { getDebugReport } from '@/api'

type DebugReportResponse = {
  jazelId: string
  generatedAt: string
  stage: string
  summary: {
    currentInventory: { total: number }
    s3: { srpObjects: { total: number } }
    opensearch?: { vins: { total: number } }
    diff: {
      missingSrp: {
        count: number
        sample: string[]
        sampleRows?: Array<{ vin: string; onPrimaryFeed: boolean | null; membershipSource?: string | null }>
      }
      extraSrp: {
        count: number
        sample: string[]
        sampleRows?: Array<{ vin: string; onPrimaryFeed: boolean | null; membershipSource?: string | null }>
      }
      missingOs?: {
        count: number
        sample: string[]
        sampleRows?: Array<{ vin: string; onPrimaryFeed: boolean | null; membershipSource?: string | null }>
      }
      extraOs?: {
        count: number
        sample: string[]
        sampleRows?: Array<{ vin: string; onPrimaryFeed: boolean | null; membershipSource?: string | null }>
      }
    }
    opensearchError?: string
    primaryFeed?: string | null
  }
  includes?: Record<string, unknown>
  includeErrors?: Record<string, string>
}

const accountStore = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const loading = ref(false)
const error = ref<string | null>(null)
const report = ref<DebugReportResponse | null>(null)

const jazelId = computed(() => accountStore.accountId)

const sample = ref(100)
const showRaw = ref(false)

const vinTableHeaders = [
  { title: 'VIN', key: 'vin' },
  { title: 'Source', key: 'membershipSource', sortable: false },
]

const asVinRows = (
  vins: string[] | undefined,
  rows?: Array<{ vin: string; onPrimaryFeed: boolean | null; membershipSource?: string | null }>,
) => {
  if (Array.isArray(rows) && rows.length > 0) return rows
  return (vins ?? []).map((vin) => ({ vin, onPrimaryFeed: null as null, membershipSource: null as null }))
}

const primaryFeed = computed(() => {
  const raws = (productSettings as any)?.primarySources
  if (!Array.isArray(raws) || raws.length === 0) return null

  const feeds = raws
    .map((s: unknown) => String(s || '').trim())
    .filter(Boolean)
    .map((s: string) => s.toLowerCase().replace(/^import_/, ''))

  return feeds.length ? feeds.join(',') : null
})

const fetchReport = async () => {
  if (!jazelId.value) {
    error.value = 'Missing Jazel ID'
    return
  }

  loading.value = true
  error.value = null

  try {
    const include = ['segment_indicators']
    report.value = await getDebugReport(endpoint, jazelId.value, {
      include,
      sample: sample.value,
      primaryFeed: primaryFeed.value,
    })
  } catch (e: any) {
    console.error(e)
    error.value = e?.message || 'Failed to fetch debug report'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (jazelId.value) fetchReport()
})
</script>

<template>
  <div class="h-100">
    <v-row align="center">
      <v-col cols="12" md="3">
        <div class="text-caption text-grey">Jazel ID (JUID)</div>
        <div class="text-body-1 font-weight-bold">{{ jazelId || '—' }}</div>
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field
          v-model.number="sample"
          label="Sample Size"
          type="number"
          variant="outlined"
          density="compact"
          hide-details
        />
      </v-col>

      <v-col cols="12" md="7" class="d-flex justify-end">
        <v-btn color="primary" variant="tonal" :loading="loading" @click="fetchReport">
          Refresh
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>

    <v-alert
      v-if="report?.summary?.opensearchError"
      type="warning"
      variant="tonal"
      class="mb-4"
    >
      OpenSearch unavailable: {{ report.summary.opensearchError }}
    </v-alert>

    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-card variant="tonal" color="info">
          <v-card-text class="text-center">
            <div class="text-h6">CurrentInventory VINs</div>
            <div v-if="loading" class="d-flex justify-center my-2">
              <v-progress-circular indeterminate color="primary" size="24" width="2" />
            </div>
            <div v-else class="text-h4">{{ report?.summary?.currentInventory?.total ?? '-' }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card variant="tonal" color="info">
          <v-card-text class="text-center">
            <div class="text-h6">S3 SRP Objects</div>
            <div v-if="loading" class="d-flex justify-center my-2">
              <v-progress-circular indeterminate color="primary" size="24" width="2" />
            </div>
            <div v-else class="text-h4">{{ report?.summary?.s3?.srpObjects?.total ?? '-' }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card variant="tonal" :color="!report ? 'info' : 'warning'">
          <v-card-text class="text-center">
            <div class="text-h6">OpenSearch VINs</div>
            <div v-if="loading" class="d-flex justify-center my-2">
              <v-progress-circular indeterminate color="primary" size="24" width="2" />
            </div>
            <div v-else class="text-h4">{{ report?.summary?.opensearch?.vins?.total ?? '—' }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-4" v-if="report?.summary?.diff?.missingOs || report?.summary?.diff?.extraOs">
      <v-col cols="12" md="6">
        <h3 class="text-h6 mb-2">Missing OpenSearch Docs (Present in CurrentInventory)</h3>
        <v-data-table
          :headers="vinTableHeaders"
          :items="asVinRows(report?.summary?.diff?.missingOs?.sample, report?.summary?.diff?.missingOs?.sampleRows)"
          :loading="loading"
          density="compact"
          class="elevation-1"
        >
          <template #item.membershipSource="{ value }">
            {{ value || '—' }}
          </template>
        </v-data-table>
        <div
          v-if="(report?.summary?.diff?.missingOs?.count ?? 0) > sample"
          class="text-caption text-center mt-2"
        >
          Showing {{ sample }} of {{ report?.summary?.diff?.missingOs?.count }} missing OpenSearch items.
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <h3 class="text-h6 mb-2">Extra OpenSearch Docs (Missing in CurrentInventory)</h3>
        <v-data-table
          :headers="vinTableHeaders"
          :items="asVinRows(report?.summary?.diff?.extraOs?.sample, report?.summary?.diff?.extraOs?.sampleRows)"
          :loading="loading"
          density="compact"
          class="elevation-1"
        >
          <template #item.membershipSource="{ value }">
            {{ value || '—' }}
          </template>
        </v-data-table>
        <div
          v-if="(report?.summary?.diff?.extraOs?.count ?? 0) > sample"
          class="text-caption text-center mt-2"
        >
          Showing {{ sample }} of {{ report?.summary?.diff?.extraOs?.count }} extra OpenSearch items.
        </div>
      </v-col>
    </v-row>    

    <v-row>
      <v-col cols="12">
        <h3 class="text-h6 mb-2">Missing SRP Objects (Present in CurrentInventory)</h3>
        <v-data-table
          :headers="vinTableHeaders"
          :items="asVinRows(report?.summary?.diff?.missingSrp?.sample, report?.summary?.diff?.missingSrp?.sampleRows)"
          :loading="loading"
          density="compact"
          class="elevation-1"
        >
          <template #item.membershipSource="{ value }">
            {{ value || '—' }}
          </template>
        </v-data-table>
        <div v-if="(report?.summary?.diff?.missingSrp?.count ?? 0) > sample" class="text-caption text-center mt-2">
          Showing {{ sample }} of {{ report?.summary?.diff?.missingSrp?.count }} missing SRP items.
        </div>
      </v-col>
    </v-row>

    <v-divider class="my-6" />

    <div class="d-flex justify-space-between align-center">
      <div class="text-caption text-grey">
        Generated: {{ report?.generatedAt ?? '—' }} (stage: {{ report?.stage ?? '—' }})
      </div>
      <v-btn variant="text" @click="showRaw = !showRaw">
        {{ showRaw ? 'Hide raw JSON' : 'Show raw JSON' }}
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-if="showRaw" class="mt-3">
        <pre class="debug-pre">{{ report ? JSON.stringify(report, null, 2) : '' }}</pre>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped>
.debug-pre {
  max-height: 520px;
  overflow: auto;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  line-height: 1.4;
}
</style>


